import CssBaseline from "@mui/joy/CssBaseline";
import { CssVarsProvider } from "@mui/joy/styles";
import {
  THEME_ID as MATERIAL_THEME_ID,
  ThemeProvider
} from "@mui/material/styles";
import React, { useEffect, useState } from "react";
import "./index.scss";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import {
  createRootRoute,
  createRoute,
  createRouter,
  RouterProvider,
  redirect
} from "@tanstack/react-router";
import { signInWithCustomToken } from "firebase/auth";
import { auth } from "firebase.config";
import { loginRoute } from "pages/Home/ui/Home";
import { LayoutPage } from "pages/Layout";
import { JoyTheme } from "../../theme/JoyTheme";
import { LightTheme } from "../../theme/LightTheme";
import { completionRoute } from "../pages/onboarding/completion";
import { domainRoute } from "../pages/onboarding/domain";
// Import onboarding routes
import { onboardingRootRoute } from "../pages/onboarding/onboarding-root";
import { provisionProdRoute } from "../pages/onboarding/provision-prod";
import { ssoRoute } from "../pages/onboarding/sso";
import { workdayRoute } from "../pages/onboarding/workday";

// Import setup-password route (magic link password setup)
import { setupPasswordRoute } from "../pages/setup-password";

// const appTitle = "Rooster";
// Create a client
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false
    }
  }
});

export const rootRoute = createRootRoute({
  component: RootComponent
});

// Index route: redirects base URL "/" to login page
const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  beforeLoad: () => {
    throw redirect({ to: "/login" });
  }
});

const TanStackRouterDevtools =
  import.meta.env.REACT_APP_ENV !== "local"
    ? () => null // Render nothing in production
    : React.lazy(() =>
        // Lazy load in development
        import("@tanstack/router-devtools").then((res) => ({
          default: res.TanStackRouterDevtools
        }))
      );

function RootComponent() {
  return (
    <React.Suspense>
      <LayoutPage />
      <TanStackRouterDevtools />
    </React.Suspense>
  );
}

const routeTree = rootRoute.addChildren([
  indexRoute, // Redirect "/" to "/login"
  loginRoute,
  setupPasswordRoute, // Magic link password setup page (public)
  onboardingRootRoute.addChildren([
    domainRoute,
    ssoRoute,
    workdayRoute,
    completionRoute,
    provisionProdRoute
  ])
]);

// Set up a Router instance
const router = createRouter({
  routeTree,
  defaultPreload: "intent",
  // Since we're using React Query, we don't want loader calls to ever be stale
  // This will ensure that the loader is always called when the route is preloaded or visited
  defaultPreloadStaleTime: 0,
  context: {
    queryClient
  }
});
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

const App: React.FC = () => {
  const [authInitialized, setAuthInitialized] = useState(false);

  useEffect(() => {
    // Restore Firebase authentication on app startup
    const restoreAuth = async () => {
      try {
        // Check if user is already authenticated
        if (auth.currentUser) {
          setAuthInitialized(true);
          return;
        }

        // Check for stored custom token
        const storedToken = sessionStorage.getItem("onboardingToken");
        if (storedToken) {
          try {
            await signInWithCustomToken(auth, storedToken);
          } catch (error: unknown) {
            // If token is invalid, clear it
            const firebaseError = error as { code?: string };
            if (
              firebaseError?.code === "auth/invalid-custom-token" ||
              firebaseError?.code === "auth/custom-token-mismatch"
            ) {
              sessionStorage.removeItem("onboardingToken");
              sessionStorage.removeItem("onboardingUser");
            }
          }
        } else {
        }
      } catch (error) {
        console.error("Error during auth restoration:", error);
      } finally {
        setAuthInitialized(true);
      }
    };

    restoreAuth();
  }, []);

  // Show loading state while auth is being restored
  if (!authInitialized) {
    return null; // Or show a loading spinner if desired
  }

  return (
    <CssVarsProvider
      theme={JoyTheme}
      disableTransitionOnChange
      defaultMode="light"
    >
      <CssBaseline />
      <ThemeProvider theme={{ [MATERIAL_THEME_ID]: LightTheme }}>
        <QueryClientProvider client={queryClient}>
          <RouterProvider router={router} />
          {["localhost", "127.0.0.1"].includes(window.location.hostname) && (
            <ReactQueryDevtools initialIsOpen={false} />
          )}
        </QueryClientProvider>
      </ThemeProvider>
    </CssVarsProvider>
  );
};

export default App;
